import { supabase } from '@/integrations/supabase/client';
import {
    IPaymentsDashboard,
    PaymentsDashboardParams,
} from '@/lib/payments/models/payments-dashboard.model';
import {
    FILTER_OPTIONS_LIMIT,
    IPaymentsFilterOptions,
} from '@/lib/payments/models/payments-filters.model';

/**
 * Lettura della dashboard pagamenti.
 *
 * I filtri (viaggio, cliente, collaboratore, finestra) viaggiano verso la RPC e non si
 * applicano dopo la fetch: i KPI sono calcolati dal database sullo stesso insieme
 * filtrato, e restringere le liste nel browser li lascerebbe indietro.
 *
 * `p_search` non viene piu' passato: la ricerca libera e' stata tolta dalla UI il
 * 03/09/2026, coperta dalle tendine per viaggio, cliente e collaboratore (che cercano gia'
 * su titolo, nome ed email dentro get_payments_filter_options). Il parametro resta nella
 * RPC, con default NULL: rimetterlo non costa una migration.
 */
export const fetchPaymentsDashboard = async (
    params: PaymentsDashboardParams
): Promise<IPaymentsDashboard> => {
    // integrations/supabase/types.ts non viene rigenerato dopo ogni migration e non
    // conosce ancora questa RPC (come le altre del piano rate). I parametri tipati
    // stanno in rpc-types.ts: GetPaymentsDashboardParams.
    const { data, error } = await supabase.rpc('get_payments_dashboard' as never, {
        p_due_window_days: params.dueWindowDays,
        p_designer_id: params.designerId,
        p_recent_limit: params.recentLimit,
        p_itinerary_id: params.itineraryId,
        p_client_key: params.clientKey,
        p_due_page: params.duePage,
        p_due_page_size: params.pageSize,
        p_itineraries_page: params.itinerariesPage,
        p_itineraries_page_size: params.pageSize,
        p_exclude_unaccepted: params.excludeUnaccepted,
    } as never);

    if (error) throw error;
    return data as unknown as IPaymentsDashboard;
};

/**
 * Valori selezionabili nelle tendine dei filtri.
 *
 * `query` resta null finche' la lista sta tutta sotto il tetto: in quel caso la si scarica
 * una volta e si filtra in memoria. Si passa a valorizzarla solo quando il server ha detto
 * `truncated`, perche' da li' in poi cercare fra le righe gia' scaricate darebbe risposte
 * false — "questo cliente non esiste" quando invece non era stato mandato.
 *
 * `includeIds` riporta indietro le righe gia' selezionate anche se fuori dal tetto: senza,
 * un link aperto con un filtro nella query string mostrerebbe un'etichetta vuota.
 */
export const fetchPaymentsFilterOptions = async (params: {
    query: string | null;
    includeIds: string[];
    clientKeys: string[];
}): Promise<IPaymentsFilterOptions> => {
    const { data, error } = await supabase.rpc('get_payments_filter_options' as never, {
        p_query: params.query,
        p_limit: FILTER_OPTIONS_LIMIT,
        p_include_ids: params.includeIds.length > 0 ? params.includeIds : null,
        p_include_client_keys: params.clientKeys.length > 0 ? params.clientKeys : null,
    } as never);

    if (error) throw error;
    return data as unknown as IPaymentsFilterOptions;
};

/** Traduce gli errori della RPC in chiavi i18n: i messaggi del database non si mostrano. */
export const paymentsDashboardErrorKey = (error: unknown): string => {
    const message = (error as { message?: string })?.message ?? '';
    return message.includes('forbidden')
        ? 'payments.errors.forbidden'
        : 'payments.errors.generic';
};
